import { partnerGroupChannelQueryKeys } from '../../../api/partner-group-channel-query-keys';
import { buildReactionChips } from './communityChatReactions';

export const PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE = 20;

const mergeReactionFields = (message, reactionPatch = {}) => {
  const reactionCounts = reactionPatch.reactionCounts ?? message.reactionCounts ?? {};
  const ownReactions = reactionPatch.ownReactions ?? message.ownReactions ?? [];
  const ownReaction =
    reactionPatch.ownReaction !== undefined
      ? reactionPatch.ownReaction
      : (message.ownReaction ?? null);
  const latestReactions = reactionPatch.latestReactions ?? message.latestReactions ?? [];

  return {
    reactionCounts,
    ownReactions,
    ownReaction,
    latestReactions,
    reactionChips:
      reactionPatch.reactionChips ??
      buildReactionChips({ reactionCounts, ownReaction, ownReactions, latestReactions }),
  };
};

const sameMessageId = (left, right) => {
  const a = String(left ?? '').trim();
  const b = String(right ?? '').trim();
  return Boolean(a) && a === b;
};

const messageExistsInPages = (pages, messageId) =>
  pages.some((page) =>
    (page?.messages ?? []).some((message) => sameMessageId(message?.id, messageId)),
  );

const mapPagesWithMessageUpdate = (pages, messageId, updater) => {
  let updated = false;

  const nextPages = pages.map((page) => {
    const messages = page?.messages ?? [];
    if (!messages.some((message) => sameMessageId(message?.id, messageId))) {
      return page;
    }

    updated = true;
    return {
      ...page,
      messages: messages.map((message) =>
        sameMessageId(message?.id, messageId) ? updater(message) : message,
      ),
    };
  });

  return updated ? nextPages : null;
};

/** Update reaction fields on a cached message across all message query pages. */
export const updatePartnerGroupChatMessageReactionsInCache = (
  queryClient,
  messageId,
  reactionPatch,
) => {
  if (!messageId || !reactionPatch) {
    return false;
  }

  let updated = false;

  queryClient.setQueriesData(
    { queryKey: [...partnerGroupChannelQueryKeys.all, 'messages'] },
    (current) => {
      if (!current?.pages?.length) {
        return current;
      }

      const nextPages = mapPagesWithMessageUpdate(current.pages, messageId, (message) => ({
        ...message,
        ...mergeReactionFields(message, reactionPatch),
      }));

      if (!nextPages) {
        return current;
      }

      updated = true;
      return {
        ...current,
        pages: nextPages,
      };
    },
  );

  return updated;
};

/** Apply optimistic reaction toggle to cached message. */
export const applyPartnerGroupChatOptimisticReactionInCache = (
  queryClient,
  messageId,
  reactionType,
  applyOptimisticReaction,
) => {
  if (!messageId || !reactionType || typeof applyOptimisticReaction !== 'function') {
    return false;
  }

  let updated = false;

  queryClient.setQueriesData(
    { queryKey: [...partnerGroupChannelQueryKeys.all, 'messages'] },
    (current) => {
      if (!current?.pages?.length) {
        return current;
      }

      const nextPages = mapPagesWithMessageUpdate(current.pages, messageId, (message) =>
        applyOptimisticReaction(message, reactionType),
      );

      if (!nextPages) {
        return current;
      }

      updated = true;
      return {
        ...current,
        pages: nextPages,
      };
    },
  );

  return updated;
};

/** Update fields on a cached message across all message query pages. */
export const updatePartnerGroupChatMessageInCache = (queryClient, messageId, updater) => {
  if (!messageId || typeof updater !== 'function') {
    return false;
  }

  let updated = false;

  queryClient.setQueriesData(
    { queryKey: [...partnerGroupChannelQueryKeys.all, 'messages'] },
    (current) => {
      if (!current?.pages?.length) {
        return current;
      }

      const nextPages = mapPagesWithMessageUpdate(current.pages, messageId, updater);

      if (!nextPages) {
        return current;
      }

      updated = true;
      return {
        ...current,
        pages: nextPages,
      };
    },
  );

  return updated;
};

const isCachedStaffIdentity = (message) => {
  if (!message || typeof message !== 'object') {
    return false;
  }

  const source = String(message.source ?? '').toLowerCase();
  if (source === 'aria' || source === 'bot') {
    return false;
  }

  const senderUid = String(message.senderUid ?? '').toLowerCase();
  if (senderUid === 'aria-bot') {
    return false;
  }

  return message.isPartnerSender === true || source === 'partner' || source === 'admin';
};

const sameSender = (message, { senderUid, senderId }) => {
  if (!message) return false;
  if (senderUid && message.senderUid && String(message.senderUid) === String(senderUid)) {
    return true;
  }
  if (senderId && message.senderId && String(message.senderId) === String(senderId)) {
    return true;
  }
  return false;
};

/** Reuse partner/admin identity already in this channel when live events omit it. */
export const findStaffIdentityInChannelCache = (
  queryClient,
  { channelId, senderUid, senderId, limit = PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE } = {},
) => {
  if (!queryClient || (!senderUid && !senderId)) {
    return null;
  }

  const normalizedChannelId = channelId ? String(channelId).trim() : null;
  const current = queryClient.getQueryData(
    partnerGroupChannelQueryKeys.messages(limit, normalizedChannelId),
  );

  for (const page of current?.pages ?? []) {
    for (const message of page?.messages ?? []) {
      if (sameSender(message, { senderUid, senderId }) && isCachedStaffIdentity(message)) {
        return message;
      }
    }
  }

  return null;
};

export const applyStaffIdentityFromChannelCache = (
  queryClient,
  message,
  { channelId, limit = PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE } = {},
) => {
  if (!message) {
    return message;
  }

  const source = String(message.source ?? '').toLowerCase();
  const alreadyStaff =
    source === 'partner' ||
    source === 'admin' ||
    (message.isPartnerSender === true && message.author && message.author !== 'Member');

  if (alreadyStaff && message.author && message.author !== 'Member') {
    return message;
  }

  const prior = findStaffIdentityInChannelCache(queryClient, {
    channelId,
    senderUid: message.senderUid,
    senderId: message.senderId,
    limit,
  });

  if (!prior) {
    return message;
  }

  return {
    ...message,
    author: message.author && message.author !== 'Member' ? message.author : prior.author,
    avatarUrl: message.avatarUrl || prior.avatarUrl,
    source: message.source || prior.source,
    isPartnerSender: true,
  };
};

/** Prepend a newest message onto the first (newest) infinite-query page. */
export const appendPartnerGroupChatMessageToCache = (
  queryClient,
  message,
  { limit = PARTNER_GROUP_CHANNEL_MESSAGES_PAGE_SIZE, channelId = null } = {},
) => {
  if (!message?.id) {
    return false;
  }

  const normalizedChannelId = channelId ? String(channelId).trim() : null;
  const queryKey = partnerGroupChannelQueryKeys.messages(limit, normalizedChannelId);
  let appended = false;

  queryClient.setQueryData(queryKey, (current) => {
    if (!current?.pages?.length) {
      return current;
    }

    if (messageExistsInPages(current.pages, message.id)) {
      return current;
    }

    const pages = [...current.pages];
    const firstPage = pages[0] ?? { messages: [], status: 'found' };
    pages[0] = {
      ...firstPage,
      messages: [message, ...(firstPage.messages ?? [])],
    };

    appended = true;
    return {
      ...current,
      pages,
    };
  });

  return appended;
};
